// src/components/results/RecordingEmptyStateTile.jsx

import React, { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import ModeTileSmall from "../recording/ModeTileSmall";
import { trackEvent } from "../../utils/analytics/track";
import {
  getRecordingModeDestination,
  getReviewMetadataPath,
} from "../../utils/navigation/recordingModeNav";
import { RECORDING_MODES } from "../../utils/recordingModes";

import Pulmonology from "../icons/Pulmonology";
import RespiratoryRate from "../icons/RespiratoryRate";
import VoiceSelectionIcon from "../icons/VoiceSelectionIcon";
import StackedLineChartIcon from "../icons/StackedLineChartIcon";
import { ModeIcon } from "../recording/ModeIcon";

const EMPTY_RECORDING_CTA_CONFIG = {
  [RECORDING_MODES.SPIROMETRY]: {
    titleKey: "levelSelection.spirometryTitle",
    titleFallback: "Spirometry",
    messageKey: "results.emptyRecordPrompt",
    messageFallback: "No data yet",
    eventName: "results_empty_click_spirometry",
    Icon: Pulmonology,
  },

  [RECORDING_MODES.TIDAL]: {
    titleKey: "levelSelection.tidalBreathingTitle",
    titleFallback: "Cardiopulmonary",
    messageKey: "results.emptyRecordPrompt",
    messageFallback: "No data yet",
    eventName: "results_empty_click_cardiopulmonary",
    Icon: RespiratoryRate,
  },

  [RECORDING_MODES.MAUDIO]: {
    titleKey: "levelSelection.manualAudioTitle",
    titleFallback: "Manual Audio",
    messageKey: "results.emptyRecordPrompt",
    messageFallback: "No data yet",
    eventName: "results_empty_click_manual_audio",
    Icon: VoiceSelectionIcon,
  },

  [RECORDING_MODES.POSTEXERTION]: {
    titleKey: "levelSelection.postExertionTitle",
    titleFallback: "Post Exertion",
    messageKey: "results.emptyRecordPrompt",
    messageFallback: "No data yet",
    eventName: "results_empty_click_post_exertion",
    Icon: StackedLineChartIcon,
  },
};

function RecordingEmptyStateTile({ mode, firstRunByVariant }) {
  const { t } = useTranslation();
  const config = EMPTY_RECORDING_CTA_CONFIG[mode];

  const reviewPath = useMemo(() => {
    if (!mode) return "/home";

    const destination = getRecordingModeDestination(mode, firstRunByVariant);
    return getReviewMetadataPath(destination, mode);
  }, [mode, firstRunByVariant]);

  if (!config) {
    return (
      <div className="muted-text">
        {t("results.emptyRecordPrompt", "No data yet")}
      </div>
    );
  }

  const Icon = config.Icon;

  return (
    <div className="w-full h-full flex flex-col items-center justify-center gap-4 px-4 text-center">
      <p className="muted-text">
        {t(config.messageKey, config.messageFallback)}
      </p>

      <div className="w-full max-w-sm">
        <ModeTileSmall
          to={reviewPath}
          title={t(config.titleKey, config.titleFallback)}
          img={
            <ModeIcon>
              <Icon />
            </ModeIcon>
          }
          onClick={() =>
            trackEvent(config.eventName, {
              mode,
              via: "results_empty_state",
            })
          }
        />
      </div>
    </div>
  );
}

export default memo(RecordingEmptyStateTile);
